/**
 * ProviderOperationList — Integration Hub
 * -----------------------------------------------------------------------------
 * Lista de recursos (operações) de um conector, reutilizada em dois lugares:
 *   - mode="select" -> formulário de conexão (checkbox, escolha antes de salvar)
 *   - mode="toggle" -> aba Recursos do conector (switch, liga/desliga na hora)
 *
 * Recursos pensados para providers com dezenas de endpoints:
 *   - Busca por nome/código/descrição
 *   - Agrupamento pelo prefixo do código (customer.*, vehicle.*, invoice.* ...)
 *   - Badges de uso (Consulta / Importação)
 *   - Ajuda contextual por recurso (help_md vindo do banco)
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (lista de recursos reutilizável).
 */

import { useMemo, useState } from 'react';
import { HelpCircle, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MarkdownBlock } from '@/components/data/ProviderDocsPanel';
import type { IntegrationOperation } from '@/hooks/integrations/useIntegrationConnections';

/** Rótulos amigáveis para os prefixos mais comuns de código de operação */
const GROUP_LABELS: Record<string, string> = {
  customer: 'Clientes',
  vehicle: 'Veículos',
  invoice: 'Cobranças',
  contract: 'Contratos',
  deal: 'Negócios',
  contact: 'Contatos',
  geral: 'Geral',
};

/** Extrai o grupo a partir do prefixo do código (`customer.list` -> customer) */
function operationGroup(code: string): string {
  const [prefix] = code.split('.');
  return prefix || 'geral';
}

interface ProviderOperationListProps {
  /** Operações declaradas pelo provider */
  operations: IntegrationOperation[];
  /** Códigos atualmente ativos */
  enabledCodes: string[];
  /** Alterna um recurso */
  onToggle: (code: string, enabled: boolean) => void;
  /** Aparência do controle */
  mode?: 'select' | 'toggle';
  /** Desabilita os controles (ex.: mutação em andamento) */
  disabled?: boolean;
  /** Exibe as ações "Marcar todos / Desmarcar todos" (apenas no formulário) */
  showBulkActions?: boolean;
  /** Altura máxima da área rolável */
  maxHeightClassName?: string;
  /** Quando informado, exibe o botão "Consultar" em cada recurso ativo (modo toggle) */
  onRun?: (operation: IntegrationOperation) => void;
}


export function ProviderOperationList({
  operations,
  enabledCodes,
  onToggle,
  mode = 'select',
  disabled = false,
  showBulkActions = true,
  maxHeightClassName = 'max-h-[420px]',
  onRun,

}: ProviderOperationListProps) {
  const [search, setSearch] = useState('');

  /** Busca sem acentos e sem diferenciar maiúsculas */
  const normalize = (value: string) =>
    value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  /** Operações filtradas pela busca, agrupadas por prefixo */
  const groups = useMemo(() => {
    const term = normalize(search.trim());
    const filtered = term
      ? operations.filter(
          (op) =>
            normalize(op.name).includes(term) ||
            normalize(op.code).includes(term) ||
            normalize(op.description ?? '').includes(term),
        )
      : operations;

    const map = new Map<string, IntegrationOperation[]>();
    filtered.forEach((op) => {
      const key = operationGroup(op.code);
      map.set(key, [...(map.get(key) ?? []), op]);
    });

    return Array.from(map.entries())
      .map(([key, items]) => ({
        key,
        label: GROUP_LABELS[key] ?? key,
        items,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [operations, search]);

  const total = operations.length;
  const activeCount = enabledCodes.length;

  if (total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nenhum recurso catalogado para este aplicativo.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Barra de busca e ações rápidas */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            maxLength={80}
            placeholder="Buscar recurso"
            className="h-8 pl-8 text-xs"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <span className="text-xs text-muted-foreground">
          {activeCount} de {total} ativos
        </span>

        {showBulkActions && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => operations.forEach((op) => onToggle(op.code, true))}
            >
              Marcar todos
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => operations.forEach((op) => onToggle(op.code, false))}
            >
              Desmarcar todos
            </Button>
          </>
        )}
      </div>

      {/* Lista agrupada e rolável */}
      <div className={`space-y-4 overflow-y-auto pr-1 ${maxHeightClassName}`}>
        {groups.length === 0 && (
          <p className="py-6 text-center text-xs text-muted-foreground">
            Nenhum recurso encontrado.
          </p>
        )}

        {groups.map((group) => (
          <div key={group.key} className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </p>

            {group.items.map((operation) => {
              const enabled = enabledCodes.includes(operation.code);

              return (
                <div
                  key={operation.code}
                  className="flex items-start gap-2.5 rounded-md border p-2.5 transition-colors hover:bg-muted/40"
                >
                  {mode === 'select' ? (
                    <Checkbox
                      checked={enabled}
                      disabled={disabled}
                      className="mt-0.5"
                      onCheckedChange={(checked) => onToggle(operation.code, checked === true)}
                    />
                  ) : null}

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-medium">{operation.name}</span>
                      {operation.is_live_query && (
                        <Badge variant="secondary" className="text-[10px]">
                          Consulta
                        </Badge>
                      )}
                      {operation.is_importable && (
                        <Badge variant="outline" className="text-[10px]">
                          Importação
                        </Badge>
                      )}
                      {operation.help_md && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground"
                              aria-label={`Ajuda sobre ${operation.name}`}
                            >
                              <HelpCircle className="h-3.5 w-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-80">
                            <MarkdownBlock content={operation.help_md} />
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>

                    {operation.description && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {operation.description}
                      </p>
                    )}
                  </div>

                  {mode === 'toggle' && (
                    <div className="flex shrink-0 items-center gap-3">
                      {/* Executar a consulta deste recurso (somente quando ativo) */}
                      {onRun && operation.is_live_query && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 text-xs"
                          disabled={disabled || !enabled}
                          onClick={() => onRun(operation)}
                        >
                          <Search className="h-3 w-3" />
                          Consultar
                        </Button>
                      )}

                      <div className="flex flex-col items-end gap-1">
                        <Switch
                          checked={enabled}
                          disabled={disabled}
                          onCheckedChange={(checked) => onToggle(operation.code, checked)}
                        />
                        <span className="text-[10px] text-muted-foreground">
                          {enabled ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </div>
                  )}

                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
